import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { getProToken, setProToken, getProPlan, setProPlan, verifyPro } from "../lib/api";

/**
 * Estado Pro/Business centralizado.
 *
 * O localStorage guarda apenas o token (uma credencial, nao uma prova):
 * o acesso pago so e confirmado apos a validacao no servidor (/api/pro/verify).
 * - token valido     -> status "pro" com plano/papel confirmados
 * - 404/403          -> token expirado/revogado: removido com seguranca
 * - falha de rede/5xx-> token NUNCA e apagado; tenta de novo em 15s
 */
const ProContext = createContext(null);

export function ProProvider({ children }) {
    const [token, setToken] = useState(() => getProToken());
    const [status, setStatus] = useState(() => (getProToken() ? "checking" : "free"));
    const [plan, setPlan] = useState(() => getProPlan() || "pro");
    const [role, setRole] = useState("owner");
    const [verified, setVerified] = useState(false);
    const retryRef = useRef(null);

    const verify = useCallback(async () => {
        const t = getProToken();
        if (!t) {
            setToken(null);
            setStatus("free");
            setVerified(false);
            return;
        }
        try {
            const data = await verifyPro(t);
            setToken(t);
            setPlan(data.plan || "pro");
            setProPlan(data.plan || "pro");
            setRole(data.role || "owner");
            setStatus("pro");
            setVerified(true);
        } catch (err) {
            const code = err?.response?.status;
            if (code === 404 || code === 403) {
                setProToken(null);
                setProPlan(null);
                setToken(null);
                setStatus("free");
                setVerified(false);
            } else {
                // falha temporaria: mantem o estado anterior e agenda nova tentativa
                setToken(t);
                setStatus("pro");
                setVerified(false);
                clearTimeout(retryRef.current);
                retryRef.current = setTimeout(verify, 15000);
            }
        }
    }, []);

    useEffect(() => {
        verify();
        return () => clearTimeout(retryRef.current);
    }, [verify]);

    // chamado pela tela de sucesso do pagamento, "Tenho um codigo" e "Remover"
    const activate = useCallback((t, planName, memberRole) => {
        setProToken(t);
        setToken(t || null);
        if (t) {
            const p = planName === "business" ? "business" : "pro";
            setPlan(p);
            setProPlan(p);
            setRole(memberRole || "owner");
            setStatus("pro");
            setVerified(true); // acabou de ser validado pelo servidor
        } else {
            setProPlan(null);
            setPlan("pro");
            setRole("owner");
            setStatus("free");
            setVerified(false);
        }
    }, []);

    const value = {
        token,
        plan,
        role,
        isPro: status === "pro",
        isBusiness: status === "pro" && plan === "business",
        checking: status === "checking",
        verified,
        activate,
        reverify: verify,
    };
    return <ProContext.Provider value={value}>{children}</ProContext.Provider>;
}

export function usePro() {
    const ctx = useContext(ProContext);
    if (!ctx) throw new Error("usePro deve ser usado dentro de <ProProvider>");
    return ctx;
}
